import { Transform, Type } from 'class-transformer'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { Aggregates } from './aggregate'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface IProcessAttachment {
  idProcessRecord: number
  tableName: string
  allowedFiles?: string[]
}

export enum ValidationStatus {
  PENDING = 'pending',
  OK = 'ok',
  NEED_CORRECTION = 'need_correction',
  OPTIONAL = 'optional'
}

export interface FormValidationItem {
  value: ValidationStatus
  name: string
  link?: string | string[]
  section?: string
  order: number
  required?: boolean
}

export interface FormValidationSection {
  name: string
  order: number
  items: FormValidationItem[]
}

export class Entity {
  @Type(() => Dayjs)
  @Transform(({ value }) => value && dayjs(value), { toClassOnly: true })
  createdAt: Dayjs;

  @Type(() => Dayjs)
  @Transform(({ value }) => value && dayjs(value), { toClassOnly: true })
  updatedAt?: Dayjs;

  id: number;

  processAttachments: IProcessAttachment[]
  formValidation: Array<Record<string, any>> = []

  createdBy: Record<string, any>;
  updatedBy?: Record<string, any>;

  declare _aggregates?: Aggregates

  formatDate (date: Dayjs, format = 'DD/MM/YYYY HH:mm'): string {
    if (!date?.isValid()) return

    const local = dayjs(date)
    const offset = dayjs().tz('America/Santiago').utcOffset()
    const localDate = local.add(offset, 'minute')

    return localDate.format(format)
  }

  formatDateDayjs (date: Dayjs) {
    if (!date?.isValid()) return

    const local = dayjs(date)
    const offset = dayjs().tz('America/Santiago').utcOffset()

    return local.add(offset, 'minute')
  }

  get created (): string {
    const { createdAt } = this
    return this.formatDate(createdAt)
  }

  get createdWithOutFormat (): Dayjs {
    const { createdAt } = this
    return this.formatDateDayjs(createdAt)
  }

  get modified (): string {
    const { updatedAt } = this
    return this.formatDate(updatedAt)
  }

  get closingDate (): string {
    const { modified, isClosed } = this

    return isClosed ? modified : null
  }

  get aggregates () {
    const { _aggregates } = this
    return _aggregates
  }

  get isClosed (): boolean {
    if (!('closingReason' in this)) return false

    const { closingReason } = this
    return Boolean(closingReason)
  }

  setAggregates (aggregates: Aggregates) {
    this._aggregates = aggregates
  }

  toString () {
    const { id } = this
    return id ? `${id}` : ''
  }
}

export class EnumEntity {
  name: string
  description: string

  private _aggregates?: Aggregates

  get aggregates () {
    const { _aggregates } = this
    return _aggregates
  }

  setAggregates (aggregates: Aggregates) {
    this._aggregates = aggregates
  }

  toString () {
    return this.name
  }
}
